/**
 * §16.2 — the confirmation in front of a BULK code or post.
 *
 * WHY THIS EXISTS, AND WHY IT IS NOW BULK ONLY
 *
 * It was written when posting was permanent: the header and its lines
 * rejected UPDATE and DELETE from the instant the status flipped. Migration
 * 0027 changed that (the operator's decision, 2026-09-13) -- a posted entry is
 * edited in place until the books close, and every change is recorded. The
 * editor's single-entry confirmation went with it, because a dialog in front
 * of a reversible act is exactly the reflex this header warns about below.
 *
 * Bulk keeps it. Posting forty entries at once is a different act from
 * posting one: a wrong rule is multiplied by forty, and correcting that is
 * forty trips into the editor. The dialog shows every line so the operator
 * answers against what will be written.
 *
 * WHAT MAKES A CONFIRMATION WORTH HAVING
 *
 * Not the pause. A dialog that says "Are you sure?" and nothing else is the
 * accept-by-reflex problem with an extra click bolted on: it trains people to
 * press the same button in the same place without reading, which is strictly
 * worse than no dialog at all because it feels like a safeguard.
 *
 * So this shows the THING, not a question about it. Every line that is about
 * to be written, the totals, and the count when there are several. If the
 * operator is going to glance rather than read, let the glance be at the
 * numbers.
 *
 * ONE COMPONENT FOR ONE ENTRY AND FOR MANY. Bulk posting is the same act at a
 * different scale and the same facts matter, so it takes a list of one or a
 * list of many rather than growing a second dialog that drifts from this one.
 */

import { useEffect, useRef } from 'react';
import { formatCents } from '../lib/money.js';

export interface PostSummaryLine {
  accountCode: string;
  accountName: string;
  debitCents: number;
  creditCents: number;
}

export interface PostSummary {
  headerId: string;
  entryDate: string;
  /** Who it is with, already falling back to the party. */
  counterparty: string | null;
  lines: PostSummaryLine[];
  /** True when the entry carries at least one receipt. */
  hasReceipt: boolean;
  /** The receipts' filenames, named so the operator sees WHICH evidence is attached. */
  receiptNames?: string[];
  /** The memo as the operator is editing it. */
  memo?: string | null;
}

export interface ConfirmPostProps {
  entries: PostSummary[];
  busy?: boolean;
  /**
   * False when the batch will only code, leaving drafts.
   *
   * The wording has to change with it. Coding is reversible and posting is
   * not, and a dialog that warned about permanence either way would teach the
   * operator to ignore the warning on the half where it is true.
   */
  post?: boolean;
  /** An extra line, for the entries a batch is going to leave alone. */
  note?: string;
  /**
   * §9.1 — the other side of each transfer in the batch. When present, a
   * checkbox offers to exclude them; unticked by default, because excluding a
   * transaction is the operator's call even when the pairing is strong.
   */
  transfers?: Array<{ headerId: string; label: string }>;
  excludeTransfers?: boolean;
  onExcludeTransfersChange?: (value: boolean) => void;
  /**
   * §9.1 — entries whose other side is ALREADY POSTED. Coding one records the
   * movement twice, so the dialog warns and offers to exclude it instead. The
   * caller leaves a ticked one out of `entries`, since it will not be coded.
   */
  duplicates?: Array<{ headerId: string; label: string }>;
  excludeDuplicates?: boolean;
  onExcludeDuplicatesChange?: (value: boolean) => void;
  /**
   * §16.2 — DROPPING AN ENTRY FROM THE BATCH WITHOUT STARTING OVER.
   *
   * Every entry in the dialog is ticked. Untick one and it is left out of the
   * batch, while staying on screen so it can be put back. Before this the
   * dialog was all-or-nothing: an operator who got to the last entry and found
   * one they did not want had to cancel, clear the selection, and re-tick the
   * rest -- so the cost of noticing something wrong at the confirmation was
   * paid by redoing the work that got them there, which is exactly the moment
   * a confirmation is supposed to be cheap.
   *
   * A dropped entry is not written and is not coded. It is not the same as the
   * transfer and duplicate exclusions below it, which RECORD a decision about
   * the entry; this one simply does nothing to it.
   *
   * Every total, count and button label below counts the ticked ones only. A
   * dialog that says "Post 12" while two are unticked is worse than no count.
   */
  excluded?: string[];
  onExcludeChange?: (headerId: string, excluded: boolean) => void;
  /**
   * When present, each entry's memo is an input. The memo is the one fact a
   * batch cannot draw from a rule, so this is where it gets written rather
   * than in forty trips to the editor afterwards.
   */
  onMemoChange?: (headerId: string, memo: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmPost({
  entries,
  busy,
  post = true,
  note,
  transfers,
  excludeTransfers,
  onExcludeTransfersChange,
  duplicates,
  excludeDuplicates,
  onExcludeDuplicatesChange,
  excluded,
  onExcludeChange,
  onMemoChange,
  onConfirm,
  onCancel,
}: ConfirmPostProps) {
  const cancel = useRef<HTMLButtonElement>(null);

  /*
   * FOCUS LANDS ON CANCEL, not on the confirming button.
   *
   * A dialog that opens with the destructive action focused can be completed
   * by the Enter key still being held from whatever opened it. Posting is the
   * one action here that cannot be undone.
   */
  useEffect(() => {
    cancel.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  /*
   * `kept` is the batch; `entries` is what is drawn. They differ only when the
   * operator has unticked something, and everything that states a quantity
   * reads `kept` -- the header, the totals and the button.
   */
  const droppedIds = new Set(excluded ?? []);
  const kept = entries.filter((entry) => !droppedIds.has(entry.headerId));
  const totalDebits = kept.reduce(
    (sum, e) => sum + e.lines.reduce((n, l) => n + l.debitCents, 0),
    0,
  );
  const withoutReceipt = kept.filter((e) => !e.hasReceipt).length;
  const many = kept.length > 1;
  // Every selected entry is a duplicate being excluded: nothing is coded.
  const excludingOnly = kept.length === 0 && (excludeDuplicates ?? false);
  // Nothing ticked is not a batch. Distinguished from `excludingOnly`, which
  // is a real action with a real effect.
  const nothingKept = kept.length === 0 && !excludingOnly;

  return (
    <div className="confirm-backdrop" onClick={onCancel}>
      <div
        className="confirm"
        role="dialog"
        aria-modal="true"
        aria-label={
          post
            ? many
              ? `Post ${kept.length} entries`
              : 'Post this entry'
            : `Categorize ${kept.length} entries`
        }
        // The backdrop closes; a click inside it must not.
        onClick={(event) => event.stopPropagation()}
      >
        <h2>
          {excludingOnly
            ? `Exclude ${duplicates?.length ?? 0} ${duplicates?.length === 1 ? 'entry' : 'entries'}?`
            : nothingKept
            ? 'Nothing is ticked'
            : post
            ? many
              ? `Post ${kept.length} entries?`
              : 'Post this entry?'
            : `Categorize ${kept.length} ${many ? 'entries' : 'entry'}?`}
        </h2>

        {/*
          The consequence, stated as it is since migration 0027: posting puts
          the entries in the books, and they stay editable until the books are
          closed, with every change recorded. Not "this cannot be undone",
          which stopped being true, and a warning that overstates is one the
          operator learns to read past.

          Coding says something different because it IS different: the entries
          stay drafts and are not in the books at all.
        */}
        <p className="muted">
          {post
            ? 'Posting puts these entries in the books. You can still edit them until the books are closed, and every change is recorded.'
            : 'These stay drafts. You can open any of them and change the categorization afterwards.'}
        </p>
        {note && <p className="muted">{note}</p>}

        <div className="confirm-body">
          {entries.map((entry) => (
            <div
              key={entry.headerId}
              className={`confirm-entry${droppedIds.has(entry.headerId) ? ' is-dropped' : ''}`}
            >
              <p className="confirm-entry-head">
                {onExcludeChange && (
                  <>
                    <input
                      type="checkbox"
                      className="confirm-entry-tick"
                      checked={!droppedIds.has(entry.headerId)}
                      disabled={busy}
                      /*
                       * Named by the entry, not "Include": a dialog with a
                       * dozen checkboxes all reading "Include" is a dozen
                       * identical announcements in a screen reader, and the
                       * date and counterparty are how the operator tells the
                       * rows apart by eye anyway.
                       */
                      aria-label={`Include ${entry.counterparty ?? 'No counterparty'} ${entry.entryDate}`}
                      onChange={(event) =>
                        onExcludeChange(entry.headerId, !event.target.checked)
                      }
                    />{' '}
                  </>
                )}
                <span>{entry.entryDate}</span>{' '}
                <strong>{entry.counterparty ?? 'No counterparty'}</strong>
                {!entry.hasReceipt && <span className="muted"> · no receipt</span>}
                {entry.receiptNames && entry.receiptNames.length > 0 && (
                  <span className="muted"> · {entry.receiptNames.join(', ')}</span>
                )}
              </p>
              {onMemoChange && (
                <label className="confirm-memo">
                  <span className="muted">Memo</span>
                  <input
                    aria-label={`Memo for ${entry.counterparty ?? 'No counterparty'} ${entry.entryDate}`}
                    value={entry.memo ?? ''}
                    onChange={(event) => onMemoChange(entry.headerId, event.target.value)}
                    disabled={busy || droppedIds.has(entry.headerId)}
                  />
                </label>
              )}
              <table className="lines">
                <tbody>
                  {entry.lines.map((line, index) => (
                    <tr key={`${entry.headerId}-${index}`}>
                      <td>
                        {line.accountCode} {line.accountName}
                      </td>
                      <td className="num">
                        {line.debitCents ? formatCents(line.debitCents) : ''}
                      </td>
                      <td className="num">
                        {line.creditCents ? formatCents(line.creditCents) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {duplicates && duplicates.length > 0 && onExcludeDuplicatesChange && (
          <div className="notice notice-warn confirm-transfers">
            <p>
              {duplicates.length === 1
                ? 'The other side of this transfer is already posted, so categorizing this entry would record the movement twice.'
                : `The other side of these ${duplicates.length} transfers is already posted, so categorizing them would record each movement twice.`}
            </p>
            <ul>
              {duplicates.map((duplicate) => (
                <li key={duplicate.headerId}>{duplicate.label}</li>
              ))}
            </ul>
            <label>
              <input
                type="checkbox"
                checked={excludeDuplicates ?? false}
                onChange={(event) => onExcludeDuplicatesChange(event.target.checked)}
              />{' '}
              {duplicates.length === 1
                ? 'Exclude it instead of categorizing it'
                : 'Exclude them instead of categorizing them'}
            </label>
          </div>
        )}

        {transfers && transfers.length > 0 && onExcludeTransfersChange && (
          <div className="confirm-transfers">
            <label>
              <input
                type="checkbox"
                checked={excludeTransfers ?? false}
                onChange={(event) => onExcludeTransfersChange(event.target.checked)}
              />{' '}
              {transfers.length === 1
                ? 'Also exclude the other side of this transfer'
                : `Also exclude the other side of these ${transfers.length} transfers`}
            </label>
            <ul className="muted">
              {transfers.map((transfer) => (
                <li key={transfer.headerId}>{transfer.label}</li>
              ))}
            </ul>
          </div>
        )}

        <p className="confirm-total">
          {many ? `${kept.length} entries · ` : ''}
          {formatCents(totalDebits)} in total
          {/*
            Receipts are stated, never enforced. §7 lets an entry be posted
            without one and lets a receipt be added to a posted entry later,
            so this is a fact the operator may want and not a gate.
          */}
          {withoutReceipt > 0 && (
            <span className="muted">
              {' · '}
              {many ? `${withoutReceipt} without a receipt` : 'no receipt attached'}
            </span>
          )}
        </p>

        <div className="row confirm-actions">
          <button type="button" ref={cancel} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="confirm-go"
            onClick={onConfirm}
            disabled={busy || nothingKept}
            title={nothingKept ? 'Tick at least one entry' : undefined}
          >
            {busy
              ? post
                ? 'Posting…'
                : 'Categorizing…'
              : nothingKept
                ? // `many` is false at zero, so without this the button read
                  // "Post it" over an empty batch -- the singular wording for
                  // the one entry that is no longer there.
                  post
                  ? 'Post 0'
                  : 'Categorize 0'
                : excludingOnly
                ? 'Exclude'
                : post
                ? many
                  ? `Post ${kept.length}`
                  : 'Post it'
                : `Categorize ${kept.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
